import { useState, useCallback, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { getClubsPaginatedApi, getClubFiltersApi } from '../services/clubService';

const useClubs = () => {
  const params = useParams();
  const [clubs, setClubs] = useState([]);
  const [clubFilters, setClubFilters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalCount: 0,
    limit: 12,
    hasNextPage: false,
    hasPrevPage: false,
  });

  // Filters state
  const [filters, setFilters] = useState({
    search: '',
    clubTypes: [],
    clubCategories: [],
    featuredOnly: false,
  });

  // Track if this is the initial mount
  const isInitialMount = useRef(true);

  const fetchClubs = useCallback(
    async (communityId, page = 1, customFilters = null, skipLoadingState = false) => {
      if (!communityId) return;

      if (!skipLoadingState) {
        setLoading(true);
      }
      setError(null);

      try {
        const requestData = {
          communityId,
          page,
          limit: pagination.limit,
          ...(customFilters || filters),
        };

        const response = await getClubsPaginatedApi(requestData);

        if (response.status) {
          setClubs(response.clubsResult || []);
          setPagination(response.pagination || {});
        } else {
          setError('Failed to fetch clubs');
          setClubs([]);
        }
      } catch (err) {
        setError(err.message || 'An error occurred while fetching clubs');
        setClubs([]);
      } finally {
        if (!skipLoadingState) {
          setLoading(false);
        }
      }
    },
    [filters, pagination.limit],
  );

  const updateFilters = useCallback((newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({
      search: '',
      clubTypes: [],
      clubCategories: [],
      featuredOnly: false,
    });
  }, []);

  const goToPage = useCallback(
    (page) => {
      if (params?.id) {
        fetchClubs(params.id, page);
      }
    },
    [params, fetchClubs],
  );

  const nextPage = useCallback(() => {
    if (pagination.hasNextPage) {
      goToPage(pagination.currentPage + 1);
    }
  }, [pagination.hasNextPage, pagination.currentPage, goToPage]);

  const prevPage = useCallback(() => {
    if (pagination.hasPrevPage) {
      goToPage(pagination.currentPage - 1);
    }
  }, [pagination.hasPrevPage, pagination.currentPage, goToPage]);

  const refreshClubs = useCallback(() => {
    if (params?.id) {
      fetchClubs(params.id, pagination.currentPage);
    }
  }, [params, pagination.currentPage, fetchClubs]);

  // Extract filter dependencies to avoid complex expressions in dependency array
  const searchFilter = filters.search;
  const clubTypesFilter = filters.clubTypes;
  const clubCategoriesFilter = filters.clubCategories;
  const featuredOnlyFilter = filters.featuredOnly;
  const communityId = params?.id;

  // Auto-fetch when filters change
  // NOTE: Debouncing is handled in the component layer (ClubCommunityAll.js)
  // This effect only triggers when filters are actually updated
  useEffect(() => {
    if (!communityId) return;

    // On initial mount, fetch immediately
    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchClubs(communityId, 1);
      return;
    }

    // Fetch with updated filters (debouncing already handled by component)
    fetchClubs(communityId, 1);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFilter, clubTypesFilter, clubCategoriesFilter, featuredOnlyFilter, communityId]);

  // Get club filter counts for filtering UI
  const getClubFilters = useCallback(
    async (targetCommunityId = null, featuredOnly = false) => {
      try {
        const resolvedCommunityId = targetCommunityId || communityId;
        if (!resolvedCommunityId) {
          setClubFilters([]);
          return [];
        }

        const response = await getClubFiltersApi({
          communityId: resolvedCommunityId,
          featuredOnly,
        });

        const filtersResult = response?.categoryFilters || [];
        setClubFilters(filtersResult);
        return filtersResult;
      } catch (err) {
        setClubFilters([]);
        return [];
      }
    },
    [communityId],
  );

  useEffect(() => {
    if (!communityId) return;
    getClubFilters(communityId, featuredOnlyFilter);
  }, [communityId, featuredOnlyFilter, getClubFilters]);

  return {
    // Data
    clubs,
    clubFilters,
    loading,
    error,
    pagination,
    filters,

    // Actions
    fetchClubs,
    updateFilters,
    clearFilters,
    goToPage,
    nextPage,
    prevPage,
    refreshClubs,

    // Helper functions
    getClubFilters,
  };
};

export default useClubs;
